import type { SessionCompaction } from "@/services/multi-agent/laneV2/types";

/**
 * The line in the transcript where a `/compact` happened.
 *
 * WHAT IT TELLS THE READER. Everything above this line is still theirs to read, but Weave no longer
 * sees those messages one by one: it carries the summary instead. The summary is shown (collapsed)
 * so the reader can check what Weave will remember, which is the whole point of making the boundary
 * visible rather than silent.
 *
 * It is drawn from the server's own record of the compaction, so it is still there after a reload.
 * Native `<details>`: keyboard and screen-reader behaviour for free, no state to hold.
 */
export function CompactionDivider({ compaction }: { compaction: SessionCompaction }) {
  return (
    <div data-testid="compaction-divider" data-compaction-turn={compaction.turn_seq} className="flex flex-col gap-1.5 py-1">
      <div className="flex items-center gap-3 text-[12px] text-muted-foreground" role="separator" aria-label="Conversation compacted">
        <span className="h-px flex-1 bg-border" aria-hidden />
        <span>Conversation compacted</span>
        <span className="h-px flex-1 bg-border" aria-hidden />
      </div>
      <details className="rounded-md border border-border px-3 py-2 text-[12px] text-muted-foreground">
        <summary className="cursor-pointer select-none text-foreground">
          Weave continues from a summary of the messages above
        </summary>
        {compaction.focus && <p className="mt-2">Asked to keep: {compaction.focus}</p>}
        <p className="mt-2 whitespace-pre-wrap leading-relaxed">{compaction.summary}</p>
      </details>
    </div>
  );
}
